import type { APIShareRegionalTrendResponse, TechName } from '@see-stella/types'
import { useEffect, useState } from 'react'
import { logError } from '../../lib/logger'

/**
 * Lazily fetch the per-region trend for the CURRENT metric, refetching when the
 * metric changes. Kept out of getProfile so the base payload stays lean — this
 * fires only when region detail is actually wanted (`enabled`, i.e. both photos
 * and metrics are shared, since the dots overlay the photo AND read a score).
 *
 * The fetcher is INJECTED so the recipient view (public `getRegionalTrend` over a
 * view token) and the owner preview (authed `previewMineRegionalTrend` over an ID
 * token) reuse the exact same render path — pass a stable `fetchTrend` (useCallback).
 * Returns undefined until it resolves; the player falls back to the profile's
 * latest-scan regionalData meanwhile. A failed fetch degrades quietly to undefined
 * — region dots are an enhancement, never a gate on the profile rendering.
 */
export function useRegionalTrend(
  techName: TechName,
  enabled: boolean,
  fetchTrend: (techName: TechName) => Promise<APIShareRegionalTrendResponse>
): APIShareRegionalTrendResponse | undefined {
  const [trend, setTrend] = useState<APIShareRegionalTrendResponse | undefined>(
    undefined
  )

  useEffect(() => {
    if (!enabled) {
      setTrend(undefined)
      return
    }
    let live = true
    // Clear the previous metric's dots immediately so a stale overlay never
    // shows against the new metric while the refetch is in flight.
    setTrend(undefined)
    fetchTrend(techName)
      .then((t) => {
        if (live) setTrend(t)
      })
      .catch((e) => {
        if (!live) return
        logError('share.regionalTrend failed', e)
        setTrend(undefined)
      })
    return () => {
      live = false
    }
  }, [techName, enabled, fetchTrend])

  return trend
}
